import {useMemo, useState, useCallback } from "react";  // replace existing `import { useMemo }`
import useFocusedField from "psims/react/util/use-focused-field";
import useConfirm from "../../shared/use-confirm";
import useMsoAnnualActivityRefData from "../shared/use-mso-annual-activity-ref-data";
import useRefinerAnnualActivities from "./annual-activities/use-refiner-annual-activities";
import { UseRefinerAnnualActivityAPI } from "./use-refiner-annual-activity-api";
import { FocusField } from "../shared/types";
import useRefinerAnnualActivityNotifications from "./use-refiner-annual-activity-notifications";
import useRefinerAnnualActivityProgress from "./use-refiner-annual-activity-progress";
import useViewMode, { ViewMode } from "../../shared/use-view-mode";
import useRefinerAnnualActivityNavigation from "./use-refiner-annual-activity-navigation";
import useSubmit from '../../shared/use-submit';
import useRefinerAnnualActivityControls from "./use-refiner-annual-activity-controls";
import useMsoComment from "../shared/mso-comment/use-mso-comment";
import { isMsoComment } from "psims/models/submission-types/mso/mso-comment";
import useServiceMessages from "../../shared/use-service-messages";
import { getRecordResults } from "psims/models/submission-types/mso/annual-activity/refiner/mso-annual-activity-refiner-submission";
import useClearData from 'psims/react/pages/primary-pages/data-submissions/shared/use-clear-data';
import useMsoStockholdingStorages from "../shared/mso-stockholding-storages/use-mso-stockholding-storages";
import useImporterImports from "../shared/imports/use-importer-imports";
import useEnforceableArrangements from "../shared/enforceable-arrangements/use-enforceable-arrangements";
import useAustralianControllingCorporations from "../shared/australian-controlling-corporations/use-australian-controlling-corporations";
import { MsoStockholdingImport } from "psims/models/submission-types/mso/annual-activity/importer/mso-stockholding-import";
import useRefinerProduction, { MsoProduction } from "../shared/production/use-refiner-production";
import type { UpdateMsoCommentAnnualActivityVM } from 'psims/gen/xapi-client/models/UpdateMsoCommentAnnualActivityVM';
import { recordActionAsEnum } from 'psims/models/api/data-submission-record-action';

const FUEL_CATEGORY_TO_COMMENT_CATEGORY: Record<number, number> = {
    1: 4,  // Gasoline   → Storage - Gasoline
    2: 6,  // Diesel     → Storage - Diesel
    3: 7,  // Kerosene   → Storage - Kerosene
    4: 8,  // Crude      → Storage - Crude
    5: 9,  // Unfinished → Storage - Unfinished
};

interface UseRefinerAnnualActivityProps {
  apiCtrl: PopulatedProps<UseRefinerAnnualActivityAPI, 'submission'>;
}

function useRefinerAnnualActivity({apiCtrl}: UseRefinerAnnualActivityProps) {
  const refData = useMsoAnnualActivityRefData({
    dataSubmission: apiCtrl.submission.dataSubmission,
    msoTargetDays: apiCtrl.submission.msoTargetDays ?? [],
  });
  const viewMode = useViewMode(apiCtrl.submission);

  const progressCtrl = useRefinerAnnualActivityProgress({apiCtrl, viewMode});

  const focusFieldCtrl = useFocusedField<FocusField>();

  const deleteCtrl = useConfirm();

  const msoStockholdingStorageCtrl = useMsoStockholdingStorages({
    dataSubmissionId: apiCtrl.submission.dataSubmission.id,
    focusFieldCtrl,
    initialStorages: apiCtrl.submission.msoStockholdingStorages ?? [],
    progressCtrl,
    refData,
});
 
  const importsCtrl = useImporterImports({
    dataSubmissionId: apiCtrl.submission.dataSubmission.id,
    focusFieldCtrl,
    initialData: (apiCtrl.submission.imports ?? []) as MsoStockholdingImport[],
    progressCtrl,
    refData,
  });

 const productionCtrl = useRefinerProduction({
    dataSubmissionId: apiCtrl.submission.dataSubmission.id,
    focusFieldCtrl,
    initialData: (apiCtrl.submission.productions ?? []) as MsoProduction[],
    progressCtrl,
    refData,
  });

  const annualActivitiesCtrl = useRefinerAnnualActivities({
    dataSubmissionId: apiCtrl.submission.dataSubmission.id,
    focusFieldCtrl,
    initialAnnualActivities: apiCtrl.submission.annualActivities,
    progressCtrl,
    refData,
    imports:importsCtrl.updateImports.map(i => i.data),
    msoTargetDays: apiCtrl.submission.msoTargetDays ?? [],
  });

  const commentsCtrl = useMsoComment({
    dataSubmissionId: apiCtrl.submission.dataSubmission.id,
    focusFieldCtrl,
    initialComment: isMsoComment(apiCtrl.submission.msoComment) ? apiCtrl.submission.msoComment : null,
    isRequired: false,
    progressCtrl,
  });




const enforceableArrangementsCtrl = useEnforceableArrangements({
  initial: (apiCtrl.submission.enforceableArrangements ?? []).map(e => ({
    ...e,
    _id: Symbol(),
    _key: `ea_${e.id ?? Math.random()}`,
  })),
  dataSubmissionId: apiCtrl.submission.dataSubmission.id,
  focusFieldCtrl,
  products: refData.products,
});

const australianControllingCorporationsCtrl = useAustralianControllingCorporations(
  (apiCtrl.submission.australianControllingCorporations ?? []).map(a => ({
    ...a,
    _id: Symbol(),
    _key: `acc_${a.id ?? Math.random()}`,
  })),
  apiCtrl.submission.dataSubmission.id,
  focusFieldCtrl,
);

  const serviceMessages = useServiceMessages({
    response: apiCtrl.updateResponse,
    responseStatus: apiCtrl.loadStatus,
    serverError: apiCtrl.updateError,
    extractRecordResults: getRecordResults
  });
  
  const anyInvalid = useMemo(() => {
  return (
    annualActivitiesCtrl.anyInvalid ||
    importsCtrl.anyInvalid ||
    productionCtrl.anyInvalid || 
    msoStockholdingStorageCtrl.anyInvalid ||
    australianControllingCorporationsCtrl.anyInvalid
  );
}, [annualActivitiesCtrl, importsCtrl, productionCtrl, , msoStockholdingStorageCtrl, australianControllingCorporationsCtrl]);

  const submitCtrl = useSubmit({
    apiCtrl,
    dataSubmission: apiCtrl.submission.dataSubmission,
    focusFieldCtrl,
    hasExternalErrors: anyInvalid,
  });

  const pageChanges = useMemo(() => {
  return [
    // index 0 — Imports
        [msoStockholdingStorageCtrl.hasChanges, annualActivitiesCtrl.hasChanges, importsCtrl.hasChanges, commentsCtrl.hasChanges],
        // index 1 — Production (placeholder)
        [productionCtrl.hasChanges],
        // index 2 — Storage
        [msoStockholdingStorageCtrl.hasChanges],
        // index 3 — Enforceable arrangements
        [enforceableArrangementsCtrl.requestEntities.length > 0],
        // index 4 — Australian Controlling Corporations
        [australianControllingCorporationsCtrl.requestEntities.length > 0],
        // index 5 — Declaration
        [submitCtrl.hasUnsavedChanges],
  ]
}, [
  annualActivitiesCtrl.hasChanges,
    commentsCtrl.hasChanges,
    importsCtrl.hasChanges,
    productionCtrl.hasChanges,
    msoStockholdingStorageCtrl.hasChanges,
    enforceableArrangementsCtrl.requestEntities.length > 0,
    australianControllingCorporationsCtrl.requestEntities.length > 0,
    submitCtrl.hasUnsavedChanges,
]);
const [commentUpdates, setCommentUpdates] = useState<Record<number, string>>({});
const handleCommentChange = useCallback((fuelCategory: number, comment: string) => {
    setCommentUpdates(prev => ({ ...prev, [fuelCategory]: comment }));
}, []);

const [importsComment, setImportsComment] = useState<string>(
    () => apiCtrl.submission.msoCommentAnnualActivities
        ?.find(c => c.commentCategoryType === 1)
        ?.reporterComments ?? ''
);
const handleImportsCommentChange = useCallback((comment: string) => {
    setImportsComment(comment);
}, []);

const [productionComment, setProductionComment] = useState<string>(
    () => apiCtrl.submission.msoCommentAnnualActivities
        ?.find(c => c.commentCategoryType === 2)
        ?.reporterComments ?? ''
);
const handleProductionCommentChange = useCallback((comment: string) => {
    setProductionComment(comment);
}, []);

const [enforceableArrangementsComment, setEnforceableArrangementsComment] = useState<string>(
    () => apiCtrl.submission.msoCommentAnnualActivities
        ?.find(c => c.commentCategoryType === 10)
        ?.reporterComments ?? ''
);
const handleEnforceableArrangementsCommentChange = useCallback((comment: string) => {
    setEnforceableArrangementsComment(comment);
}, []);

const [australianControllingCorpsComment, setAustralianControllingCorpsComment] = useState<string>(
    () => apiCtrl.submission.msoCommentAnnualActivities
        ?.find(c => c.commentCategoryType === 11)
        ?.reporterComments ?? ''
);
const handleAustralianControllingCorpsCommentChange = useCallback((comment: string) => {
    setAustralianControllingCorpsComment(comment);
}, []);

const msoCommentAnnualActivities = useMemo(() => {
    const base = apiCtrl.submission.msoCommentAnnualActivities ?? [];
    const dataSubmissionId = apiCtrl.submission.dataSubmission.id;

    // Storage comments — identified by msoStorageFuelCategoryType (1-5)
    const updated = base
        .filter(c => c.msoStorageFuelCategoryType != null && c.msoStorageFuelCategoryType in commentUpdates)
        .map(c => ({
            ...c,
            dataSubmissionId,
            commentCategoryType: FUEL_CATEGORY_TO_COMMENT_CATEGORY[c.msoStorageFuelCategoryType!],
            reporterComments: commentUpdates[c.msoStorageFuelCategoryType!],
            recordAction: recordActionAsEnum('Update'),
        }));

    const existingFuelCategories = new Set(base
        .filter(c => c.msoStorageFuelCategoryType != null)
        .map(c => c.msoStorageFuelCategoryType));

    const newStorageComments = Object.entries(commentUpdates)
        .filter(([fc]) => !existingFuelCategories.has(Number(fc)))
        .map(([fc, reporterComments]) => ({
            dataSubmissionId,
            commentCategoryType: FUEL_CATEGORY_TO_COMMENT_CATEGORY[Number(fc)],
            msoStorageFuelCategoryType: Number(fc),
            reporterComments,
            recordAction: recordActionAsEnum('Create'),
        }));

    // Imports comment — commentCategoryType: 1
    const existingImportsComment = base.find(c => c.commentCategoryType === 1);
    const importsCommentEntry = importsComment !== ''
        ? existingImportsComment
            ? { ...existingImportsComment, dataSubmissionId, msoStorageFuelCategoryType: undefined, reporterComments: importsComment, recordAction: recordActionAsEnum('Update') }
            : { dataSubmissionId, commentCategoryType: 1, msoStorageFuelCategoryType: undefined, reporterComments: importsComment, recordAction: recordActionAsEnum('Create') }
        : null;

    // Production comment — commentCategoryType: 2
    const existingProductionComment = base.find(c => c.commentCategoryType === 2);
    const productionCommentEntry = productionComment !== ''
        ? existingProductionComment
            ? { ...existingProductionComment, dataSubmissionId, msoStorageFuelCategoryType: undefined, reporterComments: productionComment, recordAction: recordActionAsEnum('Update') }
            : { dataSubmissionId, commentCategoryType: 2, msoStorageFuelCategoryType: undefined, reporterComments: productionComment, recordAction: recordActionAsEnum('Create') }
        : null;

    // Enforceable arrangements comment — commentCategoryType: 10
    const existingEAComment = base.find(c => c.commentCategoryType === 10);
    const eaCommentEntry = enforceableArrangementsComment !== ''
        ? existingEAComment
            ? { ...existingEAComment, dataSubmissionId, msoStorageFuelCategoryType: undefined, reporterComments: enforceableArrangementsComment, recordAction: recordActionAsEnum('Update') }
            : { dataSubmissionId, commentCategoryType: 10, msoStorageFuelCategoryType: undefined, reporterComments: enforceableArrangementsComment, recordAction: recordActionAsEnum('Create') }
        : null;

    // Australian Controlling Corps comment — commentCategoryType: 11
    const existingACCComment = base.find(c => c.commentCategoryType === 11);
    const accCommentEntry = australianControllingCorpsComment !== ''
        ? existingACCComment
            ? { ...existingACCComment, dataSubmissionId, msoStorageFuelCategoryType: undefined, reporterComments: australianControllingCorpsComment, recordAction: recordActionAsEnum('Update') }
            : { dataSubmissionId, commentCategoryType: 11, msoStorageFuelCategoryType: undefined, reporterComments: australianControllingCorpsComment, recordAction: recordActionAsEnum('Create') }
        : null;

    return [
        ...updated,
        ...newStorageComments,
        ...(importsCommentEntry ? [importsCommentEntry] : []),
        ...(productionCommentEntry ? [productionCommentEntry] : []),
        ...(eaCommentEntry ? [eaCommentEntry] : []),
        ...(accCommentEntry ? [accCommentEntry] : []),
    ].map(c => ({
        ...c,
        msoStorageFuelCategoryType: c.msoStorageFuelCategoryType ?? undefined,
    })) as UpdateMsoCommentAnnualActivityVM[];
}, [apiCtrl.submission.msoCommentAnnualActivities, commentUpdates, importsComment, productionComment, enforceableArrangementsComment, australianControllingCorpsComment]);
  const navCtrl = useRefinerAnnualActivityNavigation({
    annualActivitiesCtrl,
    apiCtrl,
    commentsCtrl,
    importsCtrl,                          // ← add
    pageChanges,
    progressCtrl,
    msoStockholdingStorageCtrl,
    enforceableArrangementsCtrl,          
    australianControllingCorporationsCtrl, 
    productionCtrl,
    submitCtrl,
    viewMode,
    msoCommentAnnualActivities  
  });

  const notificationsCtrl = useRefinerAnnualActivityNotifications({
    annualActivitiesCtrl,
    commentsCtrl,
    importsCtrl,                            
    navCtrl,
    progressCtrl,
    serviceMessages,
    storageSitesCtrl: msoStockholdingStorageCtrl,
    enforceableArrangementsCtrl,           
    australianControllingCorporationsCtrl,  
    submitCtrl,
  });

  const controlsCtrl = useRefinerAnnualActivityControls({apiCtrl, navCtrl, progressCtrl, viewMode});

  const clearDataCtrl = useClearData({
      onConfirm: () => {
          apiCtrl.clearAllData();
          submitCtrl.updateDeclaration(false);
      },
      submission: apiCtrl.submission?.dataSubmission,
  });

return useMemo(() => ({
    annualActivitiesCtrl,
    apiCtrl,
    clearDataCtrl,
    commentsCtrl,
    controlsCtrl,
    deleteCtrl,
    focusFieldCtrl,
    isDisabled: viewMode === 'view',
    importsCtrl,
    productionCtrl,
    navCtrl,
    notificationsCtrl,
    progressCtrl,
    refData,
    submitCtrl,
    msoStockholdingStorageCtrl,
    enforceableArrangementsCtrl,
    australianControllingCorporationsCtrl,
    storageSitesCtrl: msoStockholdingStorageCtrl,
    viewMode: viewMode as ViewMode,
    handleCommentChange,
    handleImportsCommentChange,
    handleProductionCommentChange,
    handleEnforceableArrangementsCommentChange,
    handleAustralianControllingCorpsCommentChange,
    msoCommentAnnualActivities,
    
}), [
    annualActivitiesCtrl,
    apiCtrl,
    clearDataCtrl,
    commentsCtrl,
    controlsCtrl,
    deleteCtrl,
    focusFieldCtrl,
    importsCtrl,
    productionCtrl,
    navCtrl,
    notificationsCtrl,
    progressCtrl,
    refData,
    submitCtrl,
    msoStockholdingStorageCtrl,
    enforceableArrangementsCtrl,
    australianControllingCorporationsCtrl,
    viewMode,
    handleCommentChange,
    handleImportsCommentChange,
    handleProductionCommentChange,
    handleEnforceableArrangementsCommentChange,
    handleAustralianControllingCorpsCommentChange,
    msoCommentAnnualActivities,
    
]);
}

export default useRefinerAnnualActivity
